import PhoneDisabledRoundedIcon from '@mui/icons-material/PhoneDisabledRounded'
import PhoneEnabledRoundedIcon from '@mui/icons-material/PhoneEnabledRounded'
import { Stack, Tooltip, Typography } from '@mui/material'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  dismissPortalVoiceCall,
  getPortalVoiceCallState,
} from 'src/mentor/components/voice/portalVoiceCallManager'
import SupportCallInboundBanner from 'src/supportPortal/components/call/SupportCallInboundBanner'
import SupportCallPip from 'src/supportPortal/components/call/SupportCallPip'
import SupportCallSurface from 'src/supportPortal/components/call/SupportCallSurface'
import { deriveSupportCallContact } from 'src/supportPortal/components/call/supportCallContact'
import { subscribeSupportInboundCalls } from 'src/supportPortal/components/call/supportCallInboundSignaling'

const AVAILABILITY_STORAGE_KEY = 'aumhum.supportCall.available'
const LIVE_STATUSES = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold', 'ending']

const SupportCallContext = createContext(null)

export function useSupportCall() {
  return useContext(SupportCallContext) || {}
}

function readStoredAvailability() {
  try {
    return window.localStorage?.getItem(AVAILABILITY_STORAGE_KEY) === 'true'
  } catch (error) {
    return false
  }
}

// The support-portal-wide call session. Mounted once above the router outlet so the surface + PIP
// survive navigation. Consumers (e.g. the booked-events phone click) call openCall({ booking }).
export default function SupportCallProvider({ children }) {
  const [session, setSession] = useState({ open: false, minimized: false, contact: null })
  const [available, setAvailableState] = useState(readStoredAvailability)
  const [incoming, setIncoming] = useState(null)
  const incomingRef = useRef(null)

  const startSession = useCallback((contact) => {
    if (!contact) {
      return
    }
    setSession({ open: true, minimized: false, contact })
  }, [])

  const minimize = useCallback(() => {
    setSession((current) => (current.open ? { ...current, minimized: true } : current))
  }, [])

  const restore = useCallback(() => {
    setSession((current) => ({ ...current, open: true, minimized: false }))
  }, [])

  // Declared after `restore` — `openCall`'s dependency array is evaluated during render, so it must
  // not reference `restore` before its initialization (that TDZ crash blanked all of /support-home).
  const openCall = useCallback(
    ({ booking } = {}) => {
      // Don't hijack an in-progress call to a different contact — the manager allows only one
      // call at a time. Bring the current one back instead.
      if (LIVE_STATUSES.includes(getPortalVoiceCallState()?.status)) {
        toast.info('Finish the current call before starting another.')
        restore()
        return
      }
      const contact = deriveSupportCallContact(booking)
      startSession(contact)
    },
    [restore, startSession],
  )

  const closeCall = useCallback(() => {
    // Never orphan a live call; dismiss only settles an already-ended/idle manager session.
    const status = getPortalVoiceCallState()?.status
    if (status === 'ended' || status === 'idle') {
      dismissPortalVoiceCall()
    }
    setSession({ open: false, minimized: false, contact: null })
  }, [])

  const setAvailable = useCallback((next) => {
    setAvailableState((current) => {
      const value = typeof next === 'boolean' ? next : !current
      try {
        window.localStorage?.setItem(AVAILABILITY_STORAGE_KEY, String(value))
      } catch (error) {
        // ignore persistence failures
      }
      return value
    })
  }, [])

  // Inbound: subscribe to the support signalling bus only while available (no always-on socket).
  useEffect(() => {
    if (!available) {
      return undefined
    }
    const unsubscribe = subscribeSupportInboundCalls({
      onIncoming: (payload) => {
        incomingRef.current = payload
        setIncoming(payload)
      },
      onCancelled: (payload) => {
        if (!payload?.callId || incomingRef.current?.callId === payload.callId) {
          incomingRef.current = null
          setIncoming(null)
        }
      },
    })
    return unsubscribe
  }, [available])

  const handleAcceptInbound = useCallback(
    (payload) => {
      setIncoming(null)
      incomingRef.current = null
      startSession({
        bookedEventId: payload?.bookedEventId || '',
        patientId: payload?.patientId || '',
        practitionerId: payload?.practitionerId || '',
        clinicBrandId: payload?.clinicBrandId || '',
        clinicId: payload?.clinicId || '',
        clinicBrandName: payload?.clinicBrandName || '',
        eventType: payload?.eventType || '',
        targetRole: payload?.callerRole || 'patient',
        name: payload?.callerName || 'Incoming caller',
        rawPhone: payload?.rawPhone || '',
      })
    },
    [startSession],
  )

  const handleRejectInbound = useCallback(() => {
    setIncoming(null)
    incomingRef.current = null
  }, [])

  const contextValue = useMemo(
    () => ({
      openCall,
      closeCall,
      minimize,
      restore,
      isOpen: session.open,
      available,
      setAvailable,
    }),
    [openCall, closeCall, minimize, restore, session.open, available, setAvailable],
  )

  return (
    <SupportCallContext.Provider value={contextValue}>
      {children}
      <SupportCallRoot
        session={session}
        available={available}
        incoming={incoming}
        onMinimize={minimize}
        onRestore={restore}
        onClose={closeCall}
        onToggleAvailable={() => setAvailable()}
        onAcceptInbound={handleAcceptInbound}
        onRejectInbound={handleRejectInbound}
      />
    </SupportCallContext.Provider>
  )
}

function SupportCallRoot({
  session,
  available,
  incoming,
  onMinimize,
  onRestore,
  onClose,
  onToggleAvailable,
  onAcceptInbound,
  onRejectInbound,
}) {
  const showSurface = session.open && !session.minimized && !!session.contact
  const showPip = session.open && session.minimized && !!session.contact

  return (
    <>
      {showSurface ? (
        <SupportCallSurface contact={session.contact} onMinimize={onMinimize} onClose={onClose} />
      ) : null}
      {showPip ? <SupportCallPip contact={session.contact} onRestore={onRestore} onClose={onClose} /> : null}

      <SupportCallInboundBanner incoming={incoming} onAccept={onAcceptInbound} onReject={onRejectInbound} />

      <AvailabilityToggle available={available} onToggle={onToggleAvailable} />
    </>
  )
}

// Subtle, always-present control for inbound availability. Sits bottom-left, out of the PIP's way.
function AvailabilityToggle({ available, onToggle }) {
  return (
    <Tooltip title={available ? 'Available for incoming calls — click to go off' : 'Not receiving calls — click to go available'}>
      <Stack
        component="button"
        type="button"
        direction="row"
        spacing={0.6}
        alignItems="center"
        onClick={onToggle}
        sx={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          zIndex: 1200,
          px: 1.1,
          py: 0.6,
          borderRadius: '999px',
          cursor: 'pointer',
          border: '1px solid',
          borderColor: available ? 'rgba(5, 150, 105, 0.4)' : 'rgba(148, 163, 184, 0.4)',
          backgroundColor: available ? 'rgba(5, 150, 105, 0.08)' : 'rgba(255, 255, 255, 0.92)',
          color: available ? '#047857' : '#94a3b8',
          opacity: available ? 1 : 0.75,
          fontFamily: 'inherit',
          boxShadow: '0 6px 18px rgba(15, 23, 42, 0.1)',
          transition: 'opacity 0.15s ease, background-color 0.15s ease',
          '&:hover': { opacity: 1 },
        }}
      >
        {available ? (
          <PhoneEnabledRoundedIcon sx={{ fontSize: 15 }} />
        ) : (
          <PhoneDisabledRoundedIcon sx={{ fontSize: 15 }} />
        )}
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'inherit' }}>
          {available ? 'Calls on' : 'Calls off'}
        </Typography>
      </Stack>
    </Tooltip>
  )
}
